/** Vista embebida liviana: /embed?dashboardId=ab23fsd (también ?view=embed). */
export function isEmbedView(pathname, search) {
  if (typeof window === 'undefined') return false;
  if (pathname == null) pathname = window.location.pathname;
  if (search == null) search = window.location.search;
  if (/\/embed\/?$/i.test(pathname)) return true;
  return new URLSearchParams(search).get('view') === 'embed';
}

/** URL de la vista embebida (solo lectura, sin barra superior). */
export function buildDashboardEmbedUrl(dashboardOrCode, origin) {
  const code =
    typeof dashboardOrCode === 'object'
      ? getDashboardShareCode(dashboardOrCode)
      : dashboardOrCode;
  if (code == null || String(code).trim() === '') return null;
  const normalized = String(code).trim().toLowerCase();
  const baseOrigin = origin || (typeof window !== 'undefined' ? window.location.origin : '');
  const tenantMatch =
    typeof window !== 'undefined' ? window.location.pathname.match(/^\/([^/]+)/) : null;
  const tenantPrefix = tenantMatch ? `/${tenantMatch[1]}` : '';
  return `${baseOrigin}${tenantPrefix}/embed?dashboardId=${encodeURIComponent(normalized)}`;
}

/** ID de dashboard en query string (?dashboardId=ab23fsd). */
export function parseDashboardIdFromUrl(search) {
  if (search == null) {
    if (typeof window === 'undefined') return null;
    search = window.location.search;
  }
  const raw = new URLSearchParams(search).get('dashboardId');
  if (raw == null || String(raw).trim() === '') return null;
  return String(raw).trim().toLowerCase();
}

/** Código público del dashboard para compartir (ej. ab23fsd). */
export function getDashboardShareCode(dashboard) {
  if (!dashboard) return null;
  if (dashboard.publicId && String(dashboard.publicId).trim()) {
    return String(dashboard.publicId).trim().toLowerCase();
  }
  return null;
}

const LAST_DASHBOARD_STORAGE_PREFIX = 'bi.lastDashboardId';

function lastDashboardStorageKey(userId, ownerId) {
  const owner = String(ownerId || '').trim();
  if (!owner) return null;
  const user = String(userId || '').trim() || '_';
  return `${LAST_DASHBOARD_STORAGE_PREFIX}.${user}.${owner}`;
}

/** Id con el que se puede volver a pedir el dashboard (código público o id numérico). */
export function dashboardRestoreId(dashboard) {
  if (!dashboard || typeof dashboard !== 'object') return null;
  const code = getDashboardShareCode(dashboard);
  if (code) return code;
  if (dashboard.id != null && String(dashboard.id).trim() !== '') return String(dashboard.id).trim();
  return null;
}

export function readLastDashboardId(userId, ownerId) {
  if (typeof window === 'undefined') return null;
  const key = lastDashboardStorageKey(userId, ownerId);
  if (!key) return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw || !String(raw).trim()) return null;
    return String(raw).trim();
  } catch {
    return null;
  }
}

export function rememberLastDashboard(dashboard, userId, ownerId) {
  if (typeof window === 'undefined') return;
  const key = lastDashboardStorageKey(userId, ownerId);
  const id = dashboardRestoreId(dashboard);
  if (!key || !id) return;
  try {
    window.localStorage.setItem(key, id);
  } catch {
    /* quota / private mode */
  }
}

export function forgetLastDashboard(userId, ownerId) {
  if (typeof window === 'undefined') return;
  const key = lastDashboardStorageKey(userId, ownerId);
  if (!key) return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* private mode */
  }
}

/** Borra el recuerdo si el dashboard guardado es el que se está descartando. */
export function forgetLastDashboardIfMatch(userId, ownerId, dashboardOrId) {
  const stored = readLastDashboardId(userId, ownerId);
  if (!stored) return;
  const storedNorm = String(stored).trim().toLowerCase();
  const candidates = [];
  if (dashboardOrId && typeof dashboardOrId === 'object') {
    const restoreId = dashboardRestoreId(dashboardOrId);
    if (restoreId) candidates.push(restoreId);
    if (dashboardOrId.id != null) candidates.push(String(dashboardOrId.id).trim());
    if (dashboardOrId.publicId) candidates.push(String(dashboardOrId.publicId).trim());
  } else if (dashboardOrId != null && String(dashboardOrId).trim() !== '') {
    candidates.push(String(dashboardOrId).trim());
  }
  if (candidates.some((c) => String(c).trim().toLowerCase() === storedNorm)) {
    forgetLastDashboard(userId, ownerId);
  }
}

/** Actualiza ?dashboardId= en la barra de direcciones sin recargar. */
export function syncDashboardIdInUrl(dashboardOrCode) {
  if (typeof window === 'undefined') return;
  const code =
    typeof dashboardOrCode === 'object'
      ? getDashboardShareCode(dashboardOrCode)
      : dashboardOrCode;
  const url = new URL(window.location.href);
  if (code != null && String(code).trim() !== '') {
    url.searchParams.set('dashboardId', String(code).trim().toLowerCase());
  } else {
    url.searchParams.delete('dashboardId');
  }
  window.history.replaceState({}, '', url.toString());
}

const AUTH_QUERY_PARAMS = ['token', 'managerId', 'adminId', 'userId', 'isAdmin'];

/** Quita credenciales de la URL tras validar (evita dejar el token en historial/barra). */
export function stripAuthParamsFromUrl() {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  let changed = false;
  AUTH_QUERY_PARAMS.forEach((key) => {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key);
      changed = true;
    }
  });
  if (changed) {
    window.history.replaceState({}, '', url.toString());
  }
}

/**
 * URL embed con auth en query string (sin postMessage).
 * Preferir generarla en el servidor; el token quedará visible en logs/historial del cliente.
 */
export function buildDashboardEmbedAuthUrl(dashboardOrCode, auth, origin) {
  const base = buildDashboardEmbedUrl(dashboardOrCode, origin);
  if (!base || !auth?.token) return null;
  const url = new URL(base);
  url.searchParams.set('token', auth.token);
  if (auth.managerId) url.searchParams.set('managerId', auth.managerId);
  else if (auth.adminId) url.searchParams.set('adminId', auth.adminId);
  else if (auth.userId) url.searchParams.set('userId', auth.userId);
  if (auth.isAdmin === true) url.searchParams.set('isAdmin', 'true');
  else if (auth.isAdmin === false) url.searchParams.set('isAdmin', 'false');
  return url.toString();
}
